import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useResetUserPassword } from '../../lib/resources/mutations';
import { parseError } from '../../lib/api/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import type { UserResponse } from '../../lib/resources/types';

const schema = z.object({
  newPassword: z.string().min(8, 'Min 8 chars').max(200, 'Too long'),
  confirm: z.string(),
}).refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Does not match' });
type Values = z.infer<typeof schema>;

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserResponse | null;
}

export function ResetPasswordDialog({ open, onOpenChange, user }: ResetPasswordDialogProps) {
  const reset = useResetUserPassword();
  const [serverError, setServerError] = useState<string | null>(null);
  const [doneFor, setDoneFor] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirm: '' },
  });

  async function onSubmit(values: Values) {
    if (!user) return;
    setServerError(null);
    setDoneFor(null);
    try {
      await reset.mutateAsync({ id: user.id, newPassword: values.newPassword });
      setDoneFor(user.email);
      form.reset({ newPassword: '', confirm: '' });
    } catch (e) {
      const err = await parseError(e);
      setServerError(err.message || 'Reset failed');
    }
  }

  function close() {
    onOpenChange(false);
    setDoneFor(null);
    setServerError(null);
    form.reset({ newPassword: '', confirm: '' });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Reset password — {user?.legalName}</DialogTitle>
          <DialogDescription>
            Sets a new BCrypt password for this account. Share it with the user out-of-band
            (password manager, 1:1 channel) — there's no email reset flow.
          </DialogDescription>
        </DialogHeader>

        {doneFor ? (
          <div className="space-y-3">
            <div className="text-[13px]">Password updated for <span className="font-medium">{doneFor}</span>.</div>
            <DialogFooter>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>New password</Label>
              <Input type="password" autoComplete="new-password" {...form.register('newPassword')} />
              {form.formState.errors.newPassword && (
                <div className="text-[11px] text-error mt-1">{form.formState.errors.newPassword.message}</div>
              )}
            </div>
            <div>
              <Label>Confirm</Label>
              <Input type="password" autoComplete="new-password" {...form.register('confirm')} />
              {form.formState.errors.confirm && (
                <div className="text-[11px] text-error mt-1">{form.formState.errors.confirm.message}</div>
              )}
            </div>
            {serverError && <div className="text-[12px] text-error" role="alert">{serverError}</div>}
            <DialogFooter>
              <Button variant="secondary" type="button" onClick={close}>Cancel</Button>
              <Button type="submit" disabled={reset.isPending}>
                {reset.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Reset password
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
